import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Tooltip } from "@fluentui/react-components";
import { DocumentPdfRegular, DocumentTableRegular } from "@fluentui/react-icons";
import { useAtomValue } from "jotai";
import { useTranslation } from "react-i18next";
import { useMainContext } from "providers/MainProvider";
import { useLoadOffersForReport } from "store/ProposalsPageHooks";
import { proposalsPageOfferItemsLoadStatusState } from "store/ProposalsPageStore";
import { useTaxonomy } from "library/utils/useTaxonomy";
import useNotificationHandler from "hooks/useNotificationHandler";
import { createPDFOffers } from "library/utils/pdf";
import { getExportableColumns } from "library/utils/exportOffers";
import {
  ExportExcelOffers,
  ExportExcelOffersHandle,
} from "./ExportExcelOffers";
import {
  ExportPeriod,
  ExportPeriodDialog,
  buildExportPeriodFilter,
} from "./ExportPeriodDialog";
import { ExportInvoicesButton } from "./ExportInvoicesButton";

type ExportKind = "excel" | "pdf";

/**
 * "Export Excel" / "Export PDF" actions for the offers list (icon-only buttons,
 * named by their tooltips, to keep the toolbar on one line). Both first ask for
 * the export period (offer request date, the last two months by default), then
 * fetch every offer of that period matching the current search, filters and
 * sort (not just the rows already scrolled into the grid) and export the
 * currently visible columns.
 */
export const ExportButtons = () => {
  const { t } = useTranslation();
  const { columns, columnsReady } = useMainContext();
  const taxonomy = useTaxonomy();
  const loadOffersForReport = useLoadOffersForReport();
  const offerItemsLoadStatus = useAtomValue(
    proposalsPageOfferItemsLoadStatusState
  );
  const { handleAddNotification } = useNotificationHandler();

  const refExcelExport = useRef<ExportExcelOffersHandle>(null);
  const [excelRows, setExcelRows] = useState<any[]>([]);
  const [excelPeriod, setExcelPeriod] = useState<ExportPeriod | undefined>();
  // which export is waiting for its period in the dialog
  const [pendingExport, setPendingExport] = useState<ExportKind | null>(null);

  const disabled = !columnsReady || offerItemsLoadStatus === "loading";

  const notifyNoOffers = useCallback(() => {
    handleAddNotification({
      id: "export-no-offers",
      type: "info",
      text: t("qtg.export.noOffers", "There are no offers to export."),
    });
  }, [handleAddNotification, t]);

  // Once the fetched rows have been rendered into the hidden ExcelExport,
  // trigger the download and clear the buffer.
  useEffect(() => {
    if (excelRows.length === 0 || !refExcelExport.current) return;
    refExcelExport.current.save();
    setExcelRows([]);
  }, [excelRows]);

  const exportExcel = (period: ExportPeriod) => {
    setExcelPeriod(period);
    loadOffersForReport({
      extraFilter: buildExportPeriodFilter(period),
      onSuccess: (rows: any[]) => {
        if (!rows || rows.length === 0) {
          notifyNoOffers();
          return;
        }
        setExcelRows(rows);
      },
    });
  };

  const exportPdf = (period: ExportPeriod) => {
    loadOffersForReport({
      extraFilter: buildExportPeriodFilter(period),
      onSuccess: (rows: any[]) => {
        if (!rows || rows.length === 0) {
          notifyNoOffers();
          return;
        }
        const invalidatedStatusId =
          taxonomy?.OfferStatus?.byCode?.["OfferStatus.INVALIDATED"]?.id;
        createPDFOffers({
          rows,
          columns: getExportableColumns(columns),
          taxonomy,
          t,
          invalidatedStatusId,
          period,
        }).open();
      },
    });
  };

  const handlePeriodConfirmed = (period: ExportPeriod) => {
    const kind = pendingExport;
    setPendingExport(null);
    if (kind === "excel") exportExcel(period);
    if (kind === "pdf") exportPdf(period);
  };

  return (
    <>
      <Tooltip content={t("greco.exportExcel", "Export Excel")} relationship="label">
        <Button
          data-help="offers-export-excel"
          appearance="secondary"
          icon={<DocumentTableRegular />}
          disabled={disabled}
          onClick={() => setPendingExport("excel")}
        />
      </Tooltip>
      <Tooltip content={t("greco.exportPDF", "Export PDF")} relationship="label">
        <Button
          data-help="offers-export-pdf"
          appearance="secondary"
          icon={<DocumentPdfRegular />}
          disabled={disabled}
          onClick={() => setPendingExport("pdf")}
        />
      </Tooltip>
      <ExportInvoicesButton />
      <ExportPeriodDialog
        open={pendingExport !== null}
        title={
          pendingExport === "pdf"
            ? t("greco.exportPDF", "Export PDF")
            : t("greco.exportExcel", "Export Excel")
        }
        onCancel={() => setPendingExport(null)}
        onConfirm={handlePeriodConfirmed}
      />
      <ExportExcelOffers
        ref={refExcelExport}
        columns={columns}
        rows={excelRows}
        period={excelPeriod}
      />
    </>
  );
};
